"use client";
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import { CONFETTI_COLORS, ConfettiSettings, Music, UTC_TIME_ZONES } from '@/lib/interfaces/group-card';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSession } from 'next-auth/react';

import { GroupCardService } from "@/lib/services/groupCardService";
import { UserService, DefaultCardSettingsData } from "@/lib/services/userService";

import { CardsList } from '@/lib/interfaces/cards';
import { toast, Toaster } from 'sonner';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragEndEvent,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    horizontalListSortingStrategy,
    useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { restrictToHorizontalAxis } from '@dnd-kit/modifiers';
import { Appearance } from '@/app/card/[slug]/envelope-settings/page.client';
import NewEnvelopeEditor from '@/app/card/[slug]/envelope-settings/components/NewEnvelopeEditor';
import BottomSheet from '@/app/components/cards-component/MusicSheet';
import ConfettiSheet from '@/app/components/cards-component/ConfettiSheet';
import Envelope from '@/app/card/[slug]/envelope-settings/components/Envelope';
import BatchCardsSheet from './components/BatchCardsSheet';
import EnvelopeStage from './components/EnvelopeStage';
import { ExtraCard, Field, Panel, Segmented, Stepper, STEPS, StepId, Switch, inputClass } from './components/OrderUI';
import {
    AlertCircle, ArrowLeft, ArrowRight, CalendarClock, ChevronLeft, ChevronRight, Download, FileSpreadsheet,
    GripVertical, ImagePlus, LayoutGrid, Loader2, Lock, Music as MusicIcon, PartyPopper, Play, RotateCcw,
    Stamp, Trash2, Upload, UserPlus, Users, Volume2, Wand2, X, ZoomIn, ZoomOut,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import dynamic from 'next/dynamic';
import { DEFAULT_ENVELOPE_APPEARANCE } from '@/lib/constants/envelope';

// Heavy, rarely-used: load the cropper only when it is opened
const ImageCropperModal = dynamic(() => import('@/app/components/ImageCropperModal'), { ssr: false });
type FormData = {
    selectedCards: CardsList[];
    cardType: 'greeting' | 'group';
    recipientsFile: File | null;
    recipients: Array<Recipient> | null;
    customCardFile: File | null;
    collectionTitle?: string;
    collectionDescription?: string;
    cardTitle: string;
    senderName: string;
    subject: string;
    message: string;
    isMusic: boolean
    isConfetti: boolean
    musicSettings: Music | null
    isCustomEnvelope: boolean
    logoFile: string | null
    timeZone: string;
};


type Recipient = { email: string; name: string; date: string; cardTitle?: string };

// --- Simple CSV Parser ---
const parseCSV = (text: string): Recipient[] => {
    const lines = text.trim().split('\n').filter(line => line.trim());
    if (lines.length < 2) return [];
    const headers = lines[0].toLowerCase().split(',').map(h => h.trim());
    return lines.slice(1).map(line => {
        const values = line.split(',').map(v => v.trim().replace(/"/g, ''));
        const row: Record<string, string> = {};
        headers.forEach((header, i) => {
            row[header] = values[i] || '';
        });
        return {
            email: row.recipient_email || '',
            name: row.recipient_name || '',
            date: row.send_date || '',
            cardTitle: row.card_title || row.title || '',
        };
    }).filter(r => r.name || r.email);
};

const getMinDateTime = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
};

// Readable send date for the people table and summary; falls back to the raw value.
const formatSendDate = (value: string) => {
    const d = new Date(value);
    if (!value || isNaN(d.getTime())) return value;
    return d.toLocaleString(undefined, { day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit' });
};

// Fisher-Yates; used for "Shuffled" design order (the server hands designs out in rotation).
const shuffled = <T,>(items: T[]): T[] => {
    const out = [...items];
    for (let i = out.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [out[i], out[j]] = [out[j], out[i]];
    }
    return out;
};


// --- SortableCard Component ---
interface SortableCardProps {
    card: CardsList;
    position: number;
    removeCard: (id: string) => void;
    isShuffle: boolean;
}

function SortableCard({ card, position, removeCard, isShuffle }: SortableCardProps) {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({
        id: card.id,
        disabled: isShuffle,
    });

    const style = {
        transform: CSS.Translate.toString(transform),
        transition,
    };

    return (
        <div ref={setNodeRef} style={style} className={`relative w-36 flex-shrink-0 sm:w-40 ${isDragging ? 'z-10' : ''}`}>
            <div
                className={`group relative aspect-[5/7] w-full overflow-hidden rounded-xl border border-paper-rule bg-paper shadow-card ${isShuffle ? '' : 'cursor-grab active:cursor-grabbing'} ${isDragging ? 'ring-2 ring-ink' : ''}`}
                {...attributes}
                {...listeners}
            >
                {card.card_image && (
                    // eslint-disable-next-line @next/next/no-img-element
                    <img src={card.card_image} alt={card.name || 'Card design'} className="h-full w-full object-cover" />
                )}
                {!isShuffle && (
                    <span className="absolute left-2 top-2 flex h-6 min-w-[1.5rem] items-center justify-center rounded-full bg-white/95 px-1.5 text-xs font-semibold text-ink shadow-sm">
                        {position}
                    </span>
                )}
                {!isShuffle && (
                    <span className="absolute bottom-2 left-1/2 flex -translate-x-1/2 items-center gap-1 rounded-full bg-ink/80 px-2 py-0.5 text-[11px] font-medium text-white opacity-0 transition-opacity group-hover:opacity-100">
                        <GripVertical className="h-3 w-3" aria-hidden="true" /> Drag
                    </span>
                )}
            </div>
            <button
                type="button"
                onClick={() => removeCard(card.id)}
                className="absolute -right-2 -top-2 z-10 flex h-7 w-7 items-center justify-center rounded-full border border-paper-rule bg-white text-gray-600 shadow-sm transition-colors hover:border-heart-600 hover:text-heart-700"
                aria-label={`Remove ${card.name || 'design'}`}
            >
                <X className="h-3.5 w-3.5" />
            </button>
        </div>
    );
}

// --- Main Page Component ---
export default function BulkOrderCreatePage() {
    const router = useRouter();
    const { status: authStatus } = useSession();
    const isAuthenticated = authStatus === 'authenticated';

    const handleLoginRedirect = () => {
        sessionStorage.setItem('redirectAfterLogin', '/bulk-card-order');
        router.push('/auth/login');
    };

    const [formData, setFormData] = useState<FormData>({
        selectedCards: [],
        cardType: 'greeting',
        recipientsFile: null,
        recipients: null,
        customCardFile: null,
        collectionTitle: '',
        collectionDescription: '',
        cardTitle: '',
        senderName: '',
        subject: 'A message for you!',
        message: '',
        isMusic: false,
        isConfetti: false,
        musicSettings: null,
        isCustomEnvelope: false,
        logoFile: null,
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
    });

    const [useSameCardTitle, setUseSameCardTitle] = useState<boolean>(true);
    const [newRecipient, setNewRecipient] = useState<Recipient>({ name: '', email: '', date: '', cardTitle: '' });
    const [newRecipientErrors, setNewRecipientErrors] = useState<Record<string, string>>({});

    const [isBottomSheetOpen, setIsBottomSheetOpen] = useState(false);
    const [isConfettiSheetOpen, setIsConfettiSheetOpen] = useState(false);
    const [isLibraryOpen, setIsLibraryOpen] = useState<boolean>(false)
    const [musicSettings, setMusicSettings] = useState<Music | null>(null);
    const [musicList, setMusicList] = useState<Music[]>([]);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [mySelectedCards, setMySelectedCards] = useState<CardsList[]>([]);
    const [isShuffle, setIsShuffle] = useState<boolean>(false);
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [isDraggingFile, setIsDraggingFile] = useState(false);

    const [currentSettings, setCurrentSettings] = useState<ConfettiSettings | undefined>({
        shapes: {
            circle: true,
            square: true,
            star: false,
            heart: false,
            emoji: false,
        },
        colors: CONFETTI_COLORS,
        style: 'snow',
    });

    const [coverImage, setCoverImage] = useState<string | null>(null);
    const [appearance, setAppearance] = useState<Appearance>({ ...DEFAULT_ENVELOPE_APPEARANCE });
    const [defaultAppearance, setDefaultAppearance] = useState<Appearance>({ ...DEFAULT_ENVELOPE_APPEARANCE });

    const [currentStamp, setCurrentStamp] = useState('/assets/images/stamps/1.png');

    const [selectedTab, setSelectedTab] = useState<StepId>('card');

    // Zoom and Animation Restart States for Envelope
    const [zoom, setZoom] = useState<number>(100);
    const [animationKey, setAnimationKey] = useState<number>(0);

    const handleZoomOut = () => {
        setZoom(prev => Math.max(prev - 10, 50));
    };

    const handleZoomIn = () => {
        // 100% is the largest size that fits the preview without clipping
        setZoom(prev => Math.min(prev + 10, 100));
    };

    const handlePlayAnimation = () => {
        setAnimationKey(prev => prev + 1);
    };

    const handleAppearanceChange = (newAppearance: Partial<Appearance>) => {
        setAppearance((prev) => ({ ...prev, ...newAppearance }));
    };

    const handleStampUpdate = (newStampUrl: string) => {
        setCurrentStamp(newStampUrl);
    };


    const handleResetAppearance = () => {
        setAppearance(defaultAppearance);
    };

    const [logoBlob, setLogoBlob] = useState<Blob | null>(null);

    const handleLogoChange = (newLogoBlobUrl: string | null, blob: Blob | null) => {
        if (formData.logoFile && formData.logoFile.startsWith('blob:') && formData.logoFile !== newLogoBlobUrl) {
            URL.revokeObjectURL(formData.logoFile);
        }
        setFormData(prev => ({ ...prev, logoFile: newLogoBlobUrl }));
        setLogoBlob(blob);
    };

    useEffect(() => {
        return () => {
            if (formData.logoFile && formData.logoFile.startsWith('blob:')) {
                URL.revokeObjectURL(formData.logoFile);
            }
        };
    }, [formData.logoFile]);

    const logoFileInputRef = useRef<HTMLInputElement>(null);
    const [logoCropperSrc, setLogoCropperSrc] = useState<string | null>(null);
    const [isLogoCropperOpen, setIsLogoCropperOpen] = useState(false);

    // Reset logo file input
    const resetLogoFileInput = () => {
        if (logoFileInputRef.current) {
            logoFileInputRef.current.value = "";
        }
    };

    // Handle file selection for logo upload
    const handleLogoFileSelected = async (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        const file = event.target.files?.[0];
        if (!file) return;

        // Validate file type and size
        const maxFileSize = 2 * 1024 * 1024; // 2MB
        const allowedFileTypes = ["image/jpeg", "image/png", "image/webp"];

        if (!allowedFileTypes.includes(file.type)) {
            toast.error("Only JPEG, PNG, and WebP images are allowed for logo");
            resetLogoFileInput();
            return;
        }

        if (file.size > maxFileSize) {
            toast.error("Logo file size must be less than 2MB");
            resetLogoFileInput();
            return;
        }

        const reader = new FileReader();
        reader.onload = () => {
            if (reader.result) {
                setLogoCropperSrc(reader.result as string);
                setIsLogoCropperOpen(true);
            } else {
                toast.error("Failed to read the selected file");
            }
            resetLogoFileInput();
        };
        reader.onerror = () => {
            toast.error("Error reading logo file");
            resetLogoFileInput();
        };
        reader.readAsDataURL(file);
    };

    // Handle cropped logo image blob
    const handleLogoCropComplete = (croppedBlob: Blob) => {
        setIsLogoCropperOpen(false);

        if (formData.logoFile && formData.logoFile.startsWith("blob:")) {
            URL.revokeObjectURL(formData.logoFile);
        }

        const blobUrl = URL.createObjectURL(croppedBlob);
        setLogoBlob(croppedBlob);
        setFormData((prev) => ({
            ...prev,
            logoFile: blobUrl,
        }));
        toast.success("Logo added");
    };

    // Handle removing logo
    const handleRemoveLogo = () => {
        if (formData.logoFile && formData.logoFile.startsWith("blob:")) {
            URL.revokeObjectURL(formData.logoFile);
        }
        setLogoBlob(null);
        setFormData((prev) => ({
            ...prev,
            logoFile: null,
        }));
        resetLogoFileInput();
        toast.info("Logo removed");
    };

    const hasLoadedDefaultsRef = useRef<boolean>(false);

    // Apply default card settings if returned from getDefaultCardSettings
    const applyDefaultCardSettings = (
        data: Partial<DefaultCardSettingsData> | null | undefined
    ) => {
        if (!data) return;

        // 1. Set Appearance (Envelope)
        if (data.appearance) {
            setAppearance(data.appearance);
            setDefaultAppearance(data.appearance);
        }

        // 2. Set Stamp
        if (data.stamp) {
            setCurrentStamp(data.stamp);
        }

        // 3. Set Confetti Settings
        if (data.confetti_settings) {
            setCurrentSettings(data.confetti_settings);
            setFormData((prev) => ({ ...prev, isConfetti: true }));
        }

        // 4. Set Custom Logo
        if (data.custom_logo) {
            setFormData((prev) => ({
                ...prev,
                logoFile: data.custom_logo!,
            }));
        }
    };

    // Fetch default card settings and apply them
    useEffect(() => {
        let isMounted = true;

        const loadDefaultSettings = async () => {
            // Check if user is authenticated and fetch from server
            if (isAuthenticated) {
                try {
                    const res = await UserService.getDefaultCardSettings();
                    if (isMounted && res && res.response_status && res.response_data) {
                        hasLoadedDefaultsRef.current = true;
                        applyDefaultCardSettings(res.response_data);
                        return;
                    }
                } catch (error) {
                    console.warn("Could not fetch default card settings from API, checking cache:", error);
                }
            }

            // Fallback to local storage cache if available and not yet loaded
            if (typeof window !== "undefined" && isMounted && !hasLoadedDefaultsRef.current) {
                try {
                    const cached = localStorage.getItem("loving_default_card_settings");
                    if (cached) {
                        const parsed = JSON.parse(cached);
                        hasLoadedDefaultsRef.current = true;
                        applyDefaultCardSettings(parsed);
                    }
                } catch (e) {
                    console.error("Failed to load default card settings from cache:", e);
                }
            }
        };

        if (authStatus !== "loading") {
            loadDefaultSettings();
        }

        return () => {
            isMounted = false;
        };
    }, [isAuthenticated, authStatus]);


    const validateCardStep = (): Record<string, string> => {
        const newErrors: Record<string, string> = {};
        if (typeof formData.collectionTitle !== 'string' || formData.collectionTitle.trim() === '') {
            newErrors.collectionTitle = "Give the order a name.";
        } else if (formData.collectionTitle.trim().length > 120) {
            newErrors.collectionTitle = "Keep the order name to 120 characters or less.";
        }

        if (typeof formData.senderName !== 'string' || formData.senderName.trim() === '') {
            newErrors.senderName = "Add who the cards are from.";
        } else if (formData.senderName.trim().length > 80) {
            newErrors.senderName = "Keep the sender name to 80 characters or less.";
        }

        // Notes are optional (the API accepts a blank description).
        if ((formData.collectionDescription || '').trim().length > 500) {
            newErrors.collectionDescription = "Keep notes to 500 characters or less.";
        }

        if (!formData.selectedCards || formData.selectedCards.length === 0) {
            newErrors.cards = "Choose at least one card design.";
        }
        return newErrors;
    }

    const validateRecipientsStep = (): Record<string, string> => {
        const newErrors: Record<string, string> = {};
        if (useSameCardTitle) {
            if (typeof formData.cardTitle !== 'string' || formData.cardTitle.trim() === '') {
                newErrors.cardTitle = "Add the title shown on every card.";
            } else if (formData.cardTitle.trim().length > 120) {
                newErrors.cardTitle = "Keep the card title to 120 characters or less.";
            }
        }
        if (!formData.recipients || formData.recipients.length === 0) {
            newErrors.recipients = "Add at least one person, from a file or by hand.";
        } else {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            const seen = new Set<string>();
            formData.recipients.forEach((r, idx) => {
                if (r.email && r.email.trim() !== '') {
                    if (!emailRegex.test(r.email.trim())) {
                        newErrors[`recipients_${idx}`] = "Invalid email address.";
                    }
                    const lower = r.email.trim().toLowerCase();
                    if (seen.has(lower)) {
                        newErrors[`recipients_${idx}`] = "Duplicate email in list.";
                    }
                    seen.add(lower);
                }
                if (r.date) {
                    const d = new Date(r.date);
                    if (isNaN(d.getTime())) {
                        newErrors[`recipients_${idx}_date`] = "Invalid date format.";
                    } else if (d <= new Date()) {
                        newErrors[`recipients_${idx}_date`] = "Send date must be in the future.";
                    }
                }
                if (!useSameCardTitle && (!r.cardTitle || r.cardTitle.trim() === '')) {
                    newErrors[`recipients_${idx}_cardTitle`] = "Card title is required for this person.";
                }
            });
        }
        return newErrors;
    }

    const validateSettingsStep = (): Record<string, string> => {
        const newErrors: Record<string, string> = {};
        if (formData.isMusic && !musicSettings) {
            newErrors.music = "Pick a song, or turn music off.";
        }
        return newErrors;
    }

    const goToStep = (tab: StepId) => {
        setSelectedTab(tab);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleTabChange = (newTab: StepId) => {
        // Allow moving backwards without validation
        const order = STEPS.map(s => s.id);
        const currentIndex = order.indexOf(selectedTab);
        const targetIndex = order.indexOf(newTab);
        if (targetIndex < currentIndex) {
            goToStep(newTab);
            return;
        }

        // Block forward navigation until the user is logged in
        if (!isAuthenticated) {
            toast.error('Log in to continue with your bulk order.');
            window.scrollTo({ top: 0, behavior: 'smooth' });
            return;
        }

        // Validate every step between here and the target, so the stepper can't skip a step.
        const validators: Record<StepId, () => Record<string, string>> = {
            card: validateCardStep,
            recipients: validateRecipientsStep,
            settings: validateSettingsStep,
            envelope: () => ({}),
        };
        for (let i = currentIndex; i < targetIndex; i++) {
            const stepErrors = validators[order[i]]();
            if (Object.keys(stepErrors).length > 0) {
                setErrors(prev => ({ ...prev, ...stepErrors }));
                if (order[i] !== selectedTab) goToStep(order[i]);
                return;
            }
        }

        goToStep(newTab);
    }
    const validateForm = (): Record<string, string> => {
        const cardErrors = validateCardStep();
        const recipientsErrors = validateRecipientsStep();
        const settingsErrors = validateSettingsStep();

        const allErrors = { ...cardErrors, ...recipientsErrors, ...settingsErrors };
        setErrors(allErrors);
        return allErrors;
    };

    const clearError = (...keys: string[]) => {
        setErrors(prev => {
            const next = { ...prev };
            keys.forEach(k => delete next[k]);
            return next;
        });
    };

    const handleSelectCards = (selectedCards: CardsList[]) => {
        setMySelectedCards(selectedCards);
        setFormData(prev => ({ ...prev, selectedCards }));
        if (selectedCards.length > 0) clearError('cards');
    };

    const getUniqueRecipients = (recipients: Recipient[]): Recipient[] => {
        return recipients.filter((recipient, index, self) =>
            index === self.findIndex(r =>
                r.email.toLowerCase() === recipient.email.toLowerCase()
            )
        );
    };

    // Shared by the file picker and drag-and-drop.
    const applyImportedRecipients = (parsed: Recipient[]) => {
        const uniqueRecipients = getUniqueRecipients(parsed);
        // If any row has its own card_title, switch to per-person titles
        if (uniqueRecipients.some(r => r.cardTitle && r.cardTitle.trim() !== '')) {
            setUseSameCardTitle(false);
        }
        setFormData(prev => ({ ...prev, recipients: uniqueRecipients }));
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        const importErrors: Record<string, string> = {};
        uniqueRecipients.forEach((r, idx) => {
            if (r.email && !emailRegex.test(r.email)) importErrors[`recipients_${idx}`] = 'Invalid email address';
            if (r.date) {
                const d = new Date(r.date);
                if (isNaN(d.getTime()) || d <= new Date()) importErrors[`recipients_${idx}_date`] = 'Invalid or past send date';
            }
        });
        setErrors(prev => {
            const next: Record<string, string> = {};
            // Drop row errors from any previous file before adding this file's
            Object.entries(prev).forEach(([k, v]) => { if (!k.startsWith('recipients')) next[k] = v; });
            return { ...next, ...importErrors };
        });
        if (uniqueRecipients.length === 0) {
            toast.error('No people found. Check the header row matches the template.');
        } else {
            toast.success(`${uniqueRecipients.length} ${uniqueRecipients.length === 1 ? 'person' : 'people'} added from the file`);
        }
    };

    const processRecipientsFile = (file: File) => {
        const lower = file.name.toLowerCase();
        setFormData(prev => ({ ...prev, recipientsFile: file }));
        const reader = new FileReader();

        if (lower.endsWith('.csv')) {
            reader.onload = (event) => {
                applyImportedRecipients(parseCSV(event.target?.result as string));
            };
            reader.readAsText(file);
        } else if (lower.endsWith('.xlsx') || lower.endsWith('.xls')) {
            reader.onload = async (event) => {
                const data = event.target?.result;
                // Load the spreadsheet parser on demand (keeps ~400KB out of the initial bundle)
                const XLSX = await import('xlsx');
                const workbook = XLSX.read(data, { type: 'array' });
                const worksheet = workbook.Sheets[workbook.SheetNames[0]];
                const json: any[] = XLSX.utils.sheet_to_json(worksheet, { raw: false });
                applyImportedRecipients(json.map(row => ({
                    email: row.recipient_email || '',
                    name: row.recipient_name || '',
                    date: row.send_date || '',
                    cardTitle: row.card_title || row.title || '',
                })).filter(r => r.name || r.email));
            };
            reader.readAsArrayBuffer(file);
        } else {
            toast.error('Unsupported file type. Upload a .csv, .xlsx or .xls file.');
            setFormData(prev => ({ ...prev, recipientsFile: null }));
        }
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) processRecipientsFile(file);
        e.target.value = '';
    };

    const handleFileDrop = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setIsDraggingFile(false);
        const file = e.dataTransfer.files?.[0];
        if (file) processRecipientsFile(file);
    };

    const handleCustomUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file && file.type.startsWith('image/')) {
            setFormData(prev => ({ ...prev, customCardFile: file }));
            const uploading = toast.loading('Uploading your design…');
            try {
                const formData = new FormData();
                formData.append("image_file", file, file.name);

                const response = await GroupCardService.uploadImage(formData);

                if (response.response_status && response.response_data) {
                    const imageUrl = response.response_data.image_file;
                    const customId = `custom-${Date.now()}`;
                    const newCustomCard: CardsList = {
                        id: customId,
                        name: file.name,
                        card_image: imageUrl,
                        is_custom: true,
                        category: 'custom',
                        is_active: true,
                        is_staff_pick: false,
                        position: 0,
                        slug: customId,
                    };

                    setMySelectedCards((prevCards) => {
                        const next = [...prevCards, newCustomCard];
                        setFormData(prev => ({ ...prev, selectedCards: next }));
                        return next;
                    });
                    toast.success('Your design was added', { id: uploading });
                    clearError('cards');
                } else {
                    toast.error(response.response_message || 'Failed to upload image.', { id: uploading });
                }
            } catch (error: any) {
                toast.error(error?.message || 'Failed to upload image.', { id: uploading });
            } finally {
                e.target.value = "";
            }
        } else if (file) {
            toast.error('Choose an image file (JPG, PNG or WebP).');
        }
    };


    const addManualRecipient = () => {
        const manualErrors: Record<string, string> = {};
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        const trimmedEmail = newRecipient.email.trim();
        if (newRecipient.name.trim() === '') {
            manualErrors.name = "Name is required.";
        }

        if (trimmedEmail !== '') {
            if (!emailRegex.test(trimmedEmail)) {
                manualErrors.email = "Invalid email format.";
            } else if (formData.recipients?.some(r => r.email && r.email.toLowerCase() === trimmedEmail.toLowerCase())) {
                manualErrors.email = "This email is already in the list.";
            }
        }
        if (newRecipient.date && new Date(newRecipient.date) <= new Date()) {
            manualErrors.date = "Send date must be in the future.";
        }
        if (!useSameCardTitle && (!newRecipient.cardTitle || newRecipient.cardTitle.trim() === '')) {
            manualErrors.cardTitle = "Card title is required.";
        }

        setNewRecipientErrors(manualErrors);

        if (Object.keys(manualErrors).length > 0) {
            return;
        }

        const newIndex = (formData.recipients || []).length;
        setFormData(prev => ({
            ...prev,
            recipients: [
                ...(prev.recipients || []),
                {
                    ...newRecipient,
                    name: newRecipient.name.trim(),
                    email: newRecipient.email.trim(),
                    cardTitle: newRecipient.cardTitle ? newRecipient.cardTitle.trim() : (prev.cardTitle || '')
                }
            ]
        }));
        setNewRecipient({ name: '', email: '', date: '', cardTitle: '' });
        clearError('recipients', `recipients_${newIndex}`, `recipients_${newIndex}_date`, `recipients_${newIndex}_cardTitle`);
    };

    const removeRecipient = (index: number) => {
        setFormData(prev => ({
            ...prev,
            recipients: prev.recipients?.filter((_, i) => i !== index) || null
        }));
        // Row errors are keyed by index, so re-check the remaining rows rather than shifting keys.
        setErrors(prev => {
            const next: Record<string, string> = {};
            Object.entries(prev).forEach(([k, v]) => { if (!k.startsWith('recipients_')) next[k] = v; });
            return next;
        });
    };

    const handleSubmit = async () => {
        if (!isAuthenticated) {
            toast.error('Log in to create your bulk order.');
            window.scrollTo({ top: 0, behavior: 'smooth' });
            return;
        }

        setIsSubmitting(true);
        const newErrors = validateForm();
        if (Object.keys(newErrors).length > 0) {
            // Send the user back to the first step with a problem
            const firstStep: StepId =
                Object.keys(validateCardStep()).length ? 'card'
                : Object.keys(validateRecipientsStep()).length ? 'recipients'
                : 'settings';
            goToStep(firstStep);
            setIsSubmitting(false);
            return;
        }

        try {
            let customLogoUrl = formData.logoFile;
            if (logoBlob) {
                const logoFileObj = new File([logoBlob], 'custom_logo.png', { type: 'image/png' });
                const logoFormData = new FormData();
                logoFormData.append('image_file', logoFileObj, logoFileObj.name);
                const uploadRes = await GroupCardService.uploadImage(logoFormData);
                if (uploadRes.response_status && uploadRes.response_data?.image_file) {
                    customLogoUrl = uploadRes.response_data.image_file;
                } else {
                    toast.error('Failed to upload custom logo');
                    setIsSubmitting(false);
                    return;
                }
            } else if (formData.logoFile && formData.logoFile.startsWith('blob:')) {
                customLogoUrl = null;
            }

            // Designs are handed out in rotation down the list; "Shuffled" mixes that order first.
            const designs = isShuffle ? shuffled(mySelectedCards) : mySelectedCards;

            const payload = {
                collection_title: formData.collectionTitle || '',
                collection_description: formData.collectionDescription || '',
                card_title: useSameCardTitle ? (formData.cardTitle || '') : '',
                sender_name: formData.senderName || '',
                card_ids: designs.map(c => c.id),
                card_images: designs.map(c => c.card_image),
                card_categories: Array.from(new Set(designs.map(c => c.category).filter(Boolean))),
                card_image: designs[0]?.card_image || '',
                confetti_settings: formData.isConfetti ? currentSettings : null,
                envelope_settings: {
                    appearance,
                    stamp: currentStamp
                },
                custom_logo: customLogoUrl || null,
                music_settings: formData.isMusic ? musicSettings : null,
                time_zone: formData.timeZone,
                recipients: (formData.recipients || []).map(r => {
                    const recipientTitle = useSameCardTitle
                        ? (formData.cardTitle || '')
                        : (r.cardTitle || (r as any).title || formData.cardTitle || '');
                    return {
                        recipient_email: r.email || '',
                        recipient_name: r.name || '',
                        send_date: r.date || '',
                        card_title: recipientTitle,
                        email: r.email || '',
                        name: r.name || '',
                        date: r.date || '',
                        cardTitle: recipientTitle
                    };
                })
            };

            const response = await GroupCardService.createBulkOrder(payload);

            if (response.response_status) {
                toast.success(`${payload.recipients.length} ${payload.recipients.length === 1 ? 'card' : 'cards'} created`);
                setTimeout(() => {
                    router.push('/account/bulk-orders');
                }, 1200);
            } else {
                toast.error(response.response_message || 'Failed to create bulk order.');
            }
        } catch (error: any) {
            console.error('Error submitting bulk order:', error);
            toast.error(error?.message || 'Something went wrong while creating the order.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const categories = useMemo(() => {
        const uniqueCategories = Array.from(
            new Set(musicList.map((music) => music.category))
        ).sort();
        return ["All", ...uniqueCategories];
    }, [musicList]);


    const handleSelectMusic = (music: Music) => {
        setMusicSettings(music);
        clearError('music');
    };

    const handleInputChange = (
        e: React.ChangeEvent<
            HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
        >
    ) => {
        const { name, value, type } = e.target;
        const checked =
            type === "checkbox" ? (e.target as HTMLInputElement).checked : undefined;

        setFormData((prev) => ({
            ...prev,
            [name]: type === "checkbox" ? checked : value,
        }));

        if (errors[name]) clearError(name);
        if (name === 'isMusic' && !checked) clearError('music');

        if (name === 'isCustomEnvelope' && mySelectedCards.length > 0 && checked) {
            setCoverImage(mySelectedCards[0].card_image)
        }
    };

    // Fetch music list
    useEffect(() => {
        const fetchMusicList = async () => {
            try {
                const result = await GroupCardService.getMusicList();
                if (result.response_status && result.response_data) {
                    setMusicList(result.response_data);
                    if (result.response_data.length > 0) {
                        setMusicSettings(result.response_data[0]);
                    }
                }
            } catch (error) {
                console.error("Error fetching music list:", error);
            }
        };
        fetchMusicList();
    }, []);


    const handleConfettiSettingsChange = (settings: ConfettiSettings) => {
        setCurrentSettings(settings);
        setIsConfettiSheetOpen(false);
    };

    const removeCard = (cardId: string) => {
        setMySelectedCards((prevCards) => {
            const next = prevCards.filter((card) => card.id !== cardId);
            setFormData(prev => ({ ...prev, selectedCards: next }));
            return next;
        });
    };

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 10,
            },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    // --- dnd-kit drag handlers ---
    const onDragStart = () => {
        document.body.style.touchAction = 'none';
    };

    const onDragEnd = (event: DragEndEvent) => {
        document.body.style.touchAction = '';
        const { active, over } = event;
        if (over && active.id !== over.id) {
            setMySelectedCards((cards) => {
                const oldIndex = cards.findIndex((card) => card.id === active.id);
                const newIndex = cards.findIndex((card) => card.id === over.id);
                const next = arrayMove(cards, oldIndex, newIndex);
                setFormData(prev => ({ ...prev, selectedCards: next }));
                return next;
            });
        }
    };

    const onDragCancel = () => {
        document.body.style.touchAction = '';
    };


    const checkScrollAbility = () => {
        const el = scrollContainerRef.current;
        if (el) {
            const hasOverflow = el.scrollWidth > el.clientWidth;
            setCanScrollLeft(el.scrollLeft > 0);
            setCanScrollRight(hasOverflow && Math.ceil(el.scrollLeft) < (el.scrollWidth - el.clientWidth));
        } else {
            setCanScrollLeft(false);
            setCanScrollRight(false);
        }
    };

    const handleScroll = (direction: 'left' | 'right') => {
        const el = scrollContainerRef.current;
        if (el) {
            const scrollAmount = el.clientWidth * 0.5;
            el.scrollTo({
                left: el.scrollLeft + (direction === 'left' ? -scrollAmount : scrollAmount),
                behavior: 'smooth'
            });
        }
    };

    useEffect(() => {
        const timer = setTimeout(checkScrollAbility, 100);
        window.addEventListener('resize', checkScrollAbility);
        return () => {
            clearTimeout(timer);
            window.removeEventListener('resize', checkScrollAbility);
        };
    }, [mySelectedCards]);

    const handleDownloadExample = () => {
        // Example dates are always in the future, so the template imports cleanly.
        const year = new Date().getFullYear() + 1;
        const rows = useSameCardTitle
            ? [
                "recipient_email,recipient_name,send_date",
                `priya.shah@example.com,Priya Shah,${year}-03-14T09:00:00`,
                `tom.okafor@example.com,Tom Okafor,${year}-04-02T09:00:00`,
            ]
            : [
                "recipient_email,recipient_name,send_date,card_title",
                `priya.shah@example.com,Priya Shah,${year}-03-14T09:00:00,"Happy birthday, Priya"`,
                `tom.okafor@example.com,Tom Okafor,${year}-04-02T09:00:00,"Five years, Tom"`,
            ];
        const blob = new Blob([rows.join('\n') + '\n'], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.setAttribute("href", url);
        link.setAttribute("download", "lovingecards-bulk-template.csv");
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    // --- Derived values for the summary and step chrome ---
    const recipients = formData.recipients || [];
    const recipientCount = recipients.length;
    const rowIssueCount = new Set(
        Object.entries(errors).filter(([k, v]) => k.startsWith('recipients_') && v).map(([k]) => k.split('_')[1])
    ).size;
    const errorMessages = Object.values(errors).filter(Boolean);
    // Only valid future dates; past or broken ones are already flagged in the table.
    const now = new Date();
    const sendDates = recipients.map(r => new Date(r.date)).filter(d => !isNaN(d.getTime()) && d > now).sort((a, b) => a.getTime() - b.getTime());
    const formatDay = (d: Date) => d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
    const dateRange = sendDates.length === 0 ? null
        : sendDates.length === 1 || formatDay(sendDates[0]) === formatDay(sendDates[sendDates.length - 1])
            ? formatDay(sendDates[0])
            : `${formatDay(sendDates[0])} – ${formatDay(sendDates[sendDates.length - 1])}`;
    const undatedCount = recipients.filter(r => !r.date).length;
    const stepIndex = STEPS.findIndex(s => s.id === selectedTab);
    const isLastStep = stepIndex === STEPS.length - 1;
    const nextStep = STEPS[stepIndex + 1];
    const prevStep = STEPS[stepIndex - 1];
    const extras = [
        formData.isConfetti && 'Confetti',
        formData.isMusic && (musicSettings?.name ? `Music: ${musicSettings.name}` : 'Music'),
        formData.logoFile && 'Logo',
    ].filter(Boolean) as string[];
    const envelopeTitle = (useSameCardTitle ? formData.cardTitle : recipients.find(r => r.cardTitle)?.cardTitle) || 'Happy birthday';
    // The browser's zone may not be in the list; keep it selectable rather than silently showing another.
    const timeZoneOptions = UTC_TIME_ZONES.some(tz => tz.name === formData.timeZone)
        ? UTC_TIME_ZONES
        : [{ name: formData.timeZone, offset: '', locations: formData.timeZone }, ...UTC_TIME_ZONES];

    const createLabel = recipientCount > 0 ? `Create ${recipientCount} ${recipientCount === 1 ? 'card' : 'cards'}` : 'Create cards';

    const goNext = () => {
        if (isLastStep) {
            handleSubmit();
        } else {
            handleTabChange(nextStep.id);
        }
    };

    const NavButtons = ({ compact = false }: { compact?: boolean }) => (
        <div className={`flex items-center gap-3 ${compact ? '' : 'justify-between'}`}>
            {prevStep ? (
                <Button type="button" variant="ghost" size={compact ? 'default' : 'lg'} shape="pill" onClick={() => handleTabChange(prevStep.id)} className="px-4 text-gray-700">
                    <ArrowLeft aria-hidden="true" />
                    <span className={compact ? 'sr-only' : ''}>Back</span>
                </Button>
            ) : <span />}
            <Button type="button" size={compact ? 'default' : 'xl'} shape="pill" onClick={goNext} disabled={isSubmitting} className="group">
                {isSubmitting ? (
                    <><Loader2 className="animate-spin" aria-hidden="true" /> Creating…</>
                ) : isLastStep ? (
                    createLabel
                ) : (
                    <>
                        Next: {nextStep.label}
                        <ArrowRight className="transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
                    </>
                )}
            </Button>
        </div>
    );

    return (
        <div className="min-h-screen bg-paper pb-28 lg:pb-16">
            <Toaster richColors position="top-center" />

            {/* Page header */}
            <header className="border-b border-paper-rule bg-paper">
                <div className="content-shell py-4 md:py-5">
                    <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-2">
                        <div className="flex min-w-0 items-baseline gap-3">
                            <h1 className="font-display text-2xl font-medium tracking-tight text-ink md:text-[1.75rem]">New bulk order</h1>
                            <nav aria-label="Breadcrumb" className="hidden text-sm text-gray-600 sm:block">
                                <Link href="/bulk-cards" className="hover:text-ink hover:underline">Bulk cards</Link>
                                <span className="mx-1.5" aria-hidden="true">/</span>
                                <span>New order</span>
                            </nav>
                        </div>
                        {isAuthenticated && (
                            <Link href="/account/bulk-orders" className="text-sm font-semibold text-ink underline decoration-heart-300 underline-offset-4 hover:decoration-heart-600">
                                Your bulk orders
                            </Link>
                        )}
                    </div>
                    <div className="mt-4">
                        <Stepper current={selectedTab} onSelect={handleTabChange} />
                    </div>
                </div>
            </header>

            <div className={`content-shell grid gap-8 py-6 ${selectedTab === 'envelope' ? '' : 'lg:grid-cols-[minmax(0,1fr)_320px] xl:grid-cols-[minmax(0,1fr)_360px]'}`}>
                <main className="min-w-0 space-y-6">
                    {/* Login required */}
                    {authStatus === 'unauthenticated' && (
                        <div className="flex flex-col gap-4 rounded-2xl border border-paper-rule bg-paper-cream p-5 sm:flex-row sm:items-center sm:p-6">
                            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-white text-ink">
                                <Lock className="h-5 w-5" strokeWidth={1.75} aria-hidden="true" />
                            </span>
                            <div className="flex-1">
                                <h2 className="text-base font-semibold text-ink">Log in to place a bulk order</h2>
                                <p className="mt-0.5 text-sm text-gray-700">
                                    Look around first if you like. You&apos;ll need a free account to go past this step.
                                </p>
                            </div>
                            <Button type="button" shape="pill" onClick={handleLoginRedirect} className="shrink-0">
                                Log in to continue
                            </Button>
                        </div>
                    )}

                    {/* Validation summary: only real messages */}
                    {errorMessages.length > 0 && (
                        <div role="alert" className="flex gap-3 rounded-2xl border border-heart-200 bg-heart-50 p-4 sm:p-5">
                            <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-heart-700" aria-hidden="true" />
                            <div className="text-sm">
                                <p className="font-semibold text-heart-800">
                                    {errorMessages.length === 1 ? 'One thing needs a look' : `${errorMessages.length} things need a look`}
                                </p>
                                <p className="mt-0.5 text-heart-800">{errorMessages[0]}</p>
                            </div>
                        </div>
                    )}

                    {/* STEP 1: designs & details */}
                    {selectedTab === 'card' && (
                        <>
                            <Panel title="Name the order" description="How you will find this order later, and who the cards are from.">
                                <div className="grid gap-5 sm:grid-cols-2">
                                    <Field id="collectionTitle" label="Order name" error={errors.collectionTitle} hint="Only you see this.">
                                        <input
                                            type="text"
                                            id="collectionTitle"
                                            name="collectionTitle"
                                            value={formData.collectionTitle}
                                            onChange={handleInputChange}
                                            placeholder="2027 team birthdays"
                                            maxLength={120}
                                            aria-invalid={!!errors.collectionTitle}
                                            className={inputClass(!!errors.collectionTitle)}
                                        />
                                    </Field>
                                    <Field id="senderName" label="From" error={errors.senderName} hint="Appears on every card as the sender.">
                                        <input
                                            type="text"
                                            id="senderName"
                                            name="senderName"
                                            value={formData.senderName}
                                            onChange={handleInputChange}
                                            placeholder="The People Team"
                                            maxLength={80}
                                            aria-invalid={!!errors.senderName}
                                            className={inputClass(!!errors.senderName)}
                                        />
                                    </Field>
                                </div>
                                <div className="mt-5">
                                    <Field id="collectionDescription" label="Notes" optional error={errors.collectionDescription}>
                                        <textarea
                                            rows={2}
                                            id="collectionDescription"
                                            name="collectionDescription"
                                            value={formData.collectionDescription}
                                            onChange={handleInputChange}
                                            placeholder="Anything to remember about this order"
                                            maxLength={500}
                                            className={`${inputClass(!!errors.collectionDescription)} h-auto py-3`}
                                        />
                                    </Field>
                                </div>
                            </Panel>

                            <Panel
                                id="cards"
                                title="Choose designs"
                                description={mySelectedCards.length > 1
                                    ? 'Designs are given out in turn down your list, so a run of birthdays doesn’t all look the same.'
                                    : 'Pick one design for everyone, or several to mix things up.'}
                                aside={mySelectedCards.length > 0 && (
                                    <div className="flex flex-wrap gap-2">
                                        <Button type="button" variant="soft" shape="pill" onClick={() => setIsLibraryOpen(true)}>
                                            <LayoutGrid aria-hidden="true" /> Browse designs
                                        </Button>
                                        <Button type="button" variant="soft" shape="pill" asChild>
                                            <label htmlFor="custom-card" className="cursor-pointer">
                                                <ImagePlus aria-hidden="true" /> Upload your own
                                            </label>
                                        </Button>
                                    </div>
                                )}
                            >
                                <input id="custom-card" type="file" accept="image/*" onChange={handleCustomUpload} className="sr-only" />

                                {mySelectedCards.length === 0 ? (
                                    <div className={`flex flex-col items-center rounded-2xl border-2 border-dashed px-6 py-10 text-center ${errors.cards ? 'border-heart-600 bg-heart-50/40' : 'border-paper-rule bg-paper/60'}`}>
                                        <div className="mb-5 flex -space-x-6" aria-hidden="true">
                                            {['bg-heart-50 -rotate-6', 'bg-paper-cream', 'bg-sage-50 rotate-6'].map((cls) => (
                                                <span key={cls} className={`block h-24 w-[4.3rem] rounded-lg border border-paper-rule shadow-card ${cls}`} />
                                            ))}
                                        </div>
                                        <p className="font-display text-lg text-ink">No designs yet</p>
                                        <p className="mt-1 max-w-sm text-sm text-gray-600">Browse the gallery or upload your own artwork, such as a company card.</p>
                                        <div className="mt-5 flex flex-col gap-2.5 sm:flex-row">
                                            <Button type="button" shape="pill" size="lg" onClick={() => setIsLibraryOpen(true)} className="px-6">
                                                <LayoutGrid aria-hidden="true" /> Browse designs
                                            </Button>
                                            <Button type="button" variant="soft" shape="pill" size="lg" asChild className="px-6">
                                                <label htmlFor="custom-card" className="cursor-pointer">
                                                    <Upload aria-hidden="true" /> Upload your own
                                                </label>
                                            </Button>
                                        </div>
                                        {errors.cards && (
                                            <p className="mt-4 flex items-center gap-1.5 text-sm text-heart-700">
                                                <AlertCircle className="h-4 w-4" aria-hidden="true" /> {errors.cards}
                                            </p>
                                        )}
                                    </div>
                                ) : (
                                    <>
                                        <div className="relative">
                                            {canScrollLeft && (
                                                <button type="button" onClick={() => handleScroll('left')} aria-label="Scroll designs left"
                                                    className="absolute left-0 top-1/2 z-20 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full border border-paper-rule bg-white shadow-card hover:bg-paper">
                                                    <ChevronLeft className="h-5 w-5 text-ink" />
                                                </button>
                                            )}
                                            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragStart={onDragStart} onDragEnd={onDragEnd} onDragCancel={onDragCancel} modifiers={[restrictToHorizontalAxis]}>
                                                <SortableContext items={mySelectedCards.map(c => c.id)} strategy={horizontalListSortingStrategy}>
                                                    <div ref={scrollContainerRef} onScroll={checkScrollAbility}
                                                        className="flex gap-4 overflow-x-auto scroll-smooth px-1 pb-3 pt-3 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                                                        {mySelectedCards.map((card, i) => (
                                                            <SortableCard key={card.id} card={card} position={i + 1} removeCard={removeCard} isShuffle={isShuffle} />
                                                        ))}
                                                    </div>
                                                </SortableContext>
                                            </DndContext>
                                            {canScrollRight && (
                                                <button type="button" onClick={() => handleScroll('right')} aria-label="Scroll designs right"
                                                    className="absolute right-0 top-1/2 z-20 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full border border-paper-rule bg-white shadow-card hover:bg-paper">
                                                    <ChevronRight className="h-5 w-5 text-ink" />
                                                </button>
                                            )}
                                        </div>
                                        {mySelectedCards.length > 1 && (
                                            <div className="mt-4 flex flex-col gap-3 rounded-xl bg-paper px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                                                <p className="text-sm text-gray-700">
                                                    {isShuffle
                                                        ? 'Designs are mixed up before they’re handed out.'
                                                        : 'Designs go out in this order. Drag to reorder.'}
                                                </p>
                                                <Segmented
                                                    label="Design order"
                                                    value={isShuffle ? 'shuffle' : 'order'}
                                                    onChange={(v) => setIsShuffle(v === 'shuffle')}
                                                    options={[{ value: 'order', label: 'In order' }, { value: 'shuffle', label: 'Shuffled' }]}
                                                />
                                            </div>
                                        )}
                                    </>
                                )}
                            </Panel>
                        </>
                    )}

                    {/* STEP 2: people */}
                    {selectedTab === 'recipients' && (
                        <>
                            <Panel
                                title="Card title"
                                description="The headline on the front of each card."
                                aside={
                                    <Segmented
                                        label="Card title"
                                        value={useSameCardTitle ? 'same' : 'each'}
                                        onChange={(v) => setUseSameCardTitle(v === 'same')}
                                        options={[{ value: 'same', label: 'Same for everyone' }, { value: 'each', label: 'Different per person' }]}
                                    />
                                }
                            >
                                {useSameCardTitle ? (
                                    <Field id="cardTitle" label="Title on every card" error={errors.cardTitle}>
                                        <input
                                            type="text"
                                            id="cardTitle"
                                            name="cardTitle"
                                            placeholder="Happy birthday!"
                                            value={formData.cardTitle}
                                            onChange={handleInputChange}
                                            maxLength={120}
                                            aria-invalid={!!errors.cardTitle}
                                            className={inputClass(!!errors.cardTitle)}
                                        />
                                    </Field>
                                ) : (
                                    <p className="rounded-xl bg-paper px-4 py-3 text-sm text-gray-700">
                                        Add a <code className="rounded bg-white px-1.5 py-0.5 font-mono text-xs text-ink ring-1 ring-paper-rule">card_title</code> column to your file, or type a title for each person below, e.g. “Five years, Tom”.
                                    </p>
                                )}
                            </Panel>

                            <Panel
                                id="recipients"
                                title="Who gets a card"
                                description="Upload a spreadsheet, or add people one at a time. Each person becomes their own card."
                                aside={recipientCount > 0 && (
                                    <span className="inline-flex items-center gap-1.5 rounded-full bg-sage-50 px-3 py-1 text-sm font-semibold text-sage-700">
                                        <Users className="h-4 w-4" aria-hidden="true" /> {recipientCount} {recipientCount === 1 ? 'person' : 'people'}
                                    </span>
                                )}
                            >
                                <input
                                    type="file"
                                    ref={fileInputRef}
                                    onChange={handleFileChange}
                                    accept=".csv,.xlsx,.xls,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                                    className="sr-only"
                                    aria-label="Upload a CSV or Excel file"
                                />
                                <div
                                    onDragOver={(e) => { e.preventDefault(); setIsDraggingFile(true); }}
                                    onDragLeave={() => setIsDraggingFile(false)}
                                    onDrop={handleFileDrop}
                                    className={`flex flex-col items-center gap-4 rounded-2xl border-2 border-dashed px-6 py-8 text-center transition-colors sm:flex-row sm:text-left ${
                                        isDraggingFile ? 'border-ink bg-white' : errors.recipients ? 'border-heart-600 bg-heart-50/40' : 'border-paper-rule bg-paper/60'
                                    }`}
                                >
                                    <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-white text-sage-600 shadow-sm">
                                        <FileSpreadsheet className="h-6 w-6" strokeWidth={1.75} aria-hidden="true" />
                                    </span>
                                    <div className="flex-1">
                                        <p className="font-semibold text-ink">
                                            {formData.recipientsFile ? formData.recipientsFile.name : 'Drop your CSV or Excel file here'}
                                        </p>
                                        <p className="mt-0.5 text-sm text-gray-600">
                                            {formData.recipientsFile ? 'Upload another file to replace this list.' : 'Columns: recipient_email, recipient_name, send_date' + (useSameCardTitle ? '' : ', card_title')}
                                        </p>
                                    </div>
                                    <div className="flex flex-wrap justify-center gap-2">
                                        <Button type="button" shape="pill" onClick={() => fileInputRef.current?.click()}>
                                            <Upload aria-hidden="true" /> Choose file
                                        </Button>
                                        <Button type="button" variant="soft" shape="pill" onClick={handleDownloadExample}>
                                            <Download aria-hidden="true" /> Template
                                        </Button>
                                    </div>
                                </div>
                                {errors.recipients && (
                                    <p className="mt-2 flex items-center gap-1.5 text-sm text-heart-700">
                                        <AlertCircle className="h-4 w-4" aria-hidden="true" /> {errors.recipients}
                                    </p>
                                )}

                                <div className="mt-5 max-w-sm">
                                    <Field id="timeZone" label="Send times are in" hint="Every send_date in this order uses this time zone.">
                                        <select id="timeZone" name="timeZone" value={formData.timeZone} onChange={handleInputChange} className={inputClass()}>
                                            {timeZoneOptions.map((tz) => (
                                                <option key={tz.name} value={tz.name}>
                                                    {tz.offset ? `(UTC${tz.offset}) ` : ''}{tz.name.replace(/_/g, ' ')}
                                                </option>
                                            ))}
                                        </select>
                                    </Field>
                                </div>

                                {recipientCount > 0 && (
                                    <div className="mt-6 overflow-hidden rounded-2xl border border-paper-rule">
                                        {rowIssueCount > 0 && (
                                            <p className="flex items-center gap-2 border-b border-heart-200 bg-heart-50 px-4 py-2.5 text-sm font-medium text-heart-800">
                                                <AlertCircle className="h-4 w-4" aria-hidden="true" />
                                                {rowIssueCount} {rowIssueCount === 1 ? 'row needs' : 'rows need'} fixing. Correct the file and upload it again, or remove the row.
                                            </p>
                                        )}
                                        <div className="max-h-[28rem] overflow-auto">
                                            <table className="w-full min-w-[640px] text-left text-sm">
                                                <thead className="sticky top-0 z-10 bg-paper">
                                                    <tr className="text-xs font-semibold uppercase tracking-wide text-gray-600">
                                                        <th scope="col" className="px-4 py-3">Name</th>
                                                        <th scope="col" className="px-4 py-3">Email</th>
                                                        <th scope="col" className="px-4 py-3">Card title</th>
                                                        <th scope="col" className="px-4 py-3">Sends</th>
                                                        <th scope="col" className="px-4 py-3"><span className="sr-only">Remove</span></th>
                                                    </tr>
                                                </thead>
                                                <tbody className="divide-y divide-paper-rule bg-white">
                                                    {recipients.map((r, i) => {
                                                        const rowError = errors[`recipients_${i}`] || errors[`recipients_${i}_date`] || errors[`recipients_${i}_cardTitle`];
                                                        return (
                                                            <tr key={`${r.email}-${i}`} id={`recipients_${i}`} className={rowError ? 'bg-heart-50/50' : 'hover:bg-paper/60'}>
                                                                <td className="whitespace-nowrap px-4 py-3 font-medium text-ink">{r.name}</td>
                                                                <td className="whitespace-nowrap px-4 py-3 text-gray-700">
                                                                    {r.email || <span className="text-gray-500">Share by link</span>}
                                                                    {errors[`recipients_${i}`] && <span className="mt-0.5 block text-xs text-heart-700">{errors[`recipients_${i}`]}</span>}
                                                                </td>
                                                                <td className="whitespace-nowrap px-4 py-3 text-gray-700">
                                                                    {useSameCardTitle ? (formData.cardTitle || <span className="text-gray-500">Set above</span>) : (r.cardTitle || formData.cardTitle || <span className="text-gray-500">Missing</span>)}
                                                                    {errors[`recipients_${i}_cardTitle`] && <span className="mt-0.5 block text-xs text-heart-700">{errors[`recipients_${i}_cardTitle`]}</span>}
                                                                </td>
                                                                <td className="whitespace-nowrap px-4 py-3 tabular-nums text-gray-700">
                                                                    {r.date ? formatSendDate(r.date) : <span className="text-gray-500">Not scheduled</span>}
                                                                    {errors[`recipients_${i}_date`] && <span className="mt-0.5 block text-xs text-heart-700">{errors[`recipients_${i}_date`]}</span>}
                                                                </td>
                                                                <td className="px-4 py-3 text-right">
                                                                    <button type="button" onClick={() => removeRecipient(i)} aria-label={`Remove ${r.name}`}
                                                                        className="inline-flex h-8 w-8 items-center justify-center rounded-full text-gray-500 transition-colors hover:bg-heart-50 hover:text-heart-700">
                                                                        <Trash2 className="h-4 w-4" />
                                                                    </button>
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                )}

                                {/* Manual entry */}
                                <div className="mt-6 rounded-2xl bg-paper p-5">
                                    <h3 className="flex items-center gap-2 text-base font-semibold text-ink">
                                        <UserPlus className="h-4 w-4 text-heart-600" aria-hidden="true" /> Add a person
                                    </h3>
                                    <div className={`mt-4 grid gap-4 sm:grid-cols-2 ${useSameCardTitle ? 'xl:grid-cols-3' : 'xl:grid-cols-4'}`}>
                                        <Field id="manual-name" label="Name" error={newRecipientErrors.name}>
                                            <input
                                                id="manual-name"
                                                type="text"
                                                placeholder="Priya Shah"
                                                value={newRecipient.name}
                                                onChange={(e) => { setNewRecipient({ ...newRecipient, name: e.target.value }); setNewRecipientErrors(prev => ({ ...prev, name: '' })); }}
                                                className={inputClass(!!newRecipientErrors.name)}
                                            />
                                        </Field>
                                        <Field id="manual-email" label="Email" optional error={newRecipientErrors.email}>
                                            <input
                                                id="manual-email"
                                                type="email"
                                                placeholder="priya@company.com"
                                                value={newRecipient.email}
                                                onChange={(e) => { setNewRecipient({ ...newRecipient, email: e.target.value }); setNewRecipientErrors(prev => ({ ...prev, email: '' })); }}
                                                className={inputClass(!!newRecipientErrors.email)}
                                            />
                                        </Field>
                                        {!useSameCardTitle && (
                                            <Field id="manual-title" label="Card title" error={newRecipientErrors.cardTitle}>
                                                <input
                                                    id="manual-title"
                                                    type="text"
                                                    placeholder="Happy birthday, Priya"
                                                    value={newRecipient.cardTitle || ''}
                                                    onChange={(e) => { setNewRecipient({ ...newRecipient, cardTitle: e.target.value }); setNewRecipientErrors(prev => ({ ...prev, cardTitle: '' })); }}
                                                    className={inputClass(!!newRecipientErrors.cardTitle)}
                                                />
                                            </Field>
                                        )}
                                        <Field id="manual-date" label="Send on" optional error={newRecipientErrors.date}>
                                            <input
                                                id="manual-date"
                                                type="datetime-local"
                                                min={getMinDateTime()}
                                                value={newRecipient.date}
                                                onChange={(e) => { setNewRecipient({ ...newRecipient, date: e.target.value }); setNewRecipientErrors(prev => ({ ...prev, date: '' })); }}
                                                className={inputClass(!!newRecipientErrors.date)}
                                            />
                                        </Field>
                                    </div>
                                    <p className="mt-3 text-sm text-gray-600">
                                        No email? The card is created and you share its link yourself. No date? It waits until you send it.
                                    </p>
                                    <Button type="button" variant="soft" shape="pill" onClick={addManualRecipient} className="mt-4">
                                        <UserPlus aria-hidden="true" /> Add to list
                                    </Button>
                                </div>
                            </Panel>
                        </>
                    )}

                    {/* STEP 3: extras */}
                    {selectedTab === 'settings' && (
                        <Panel title="Extras" description="Chosen once and used on every card in this order. All optional.">
                            <div className="grid gap-4 md:grid-cols-2">
                                <ExtraCard
                                    icon={PartyPopper}
                                    tone="marigold"
                                    title="Confetti"
                                    body="A burst of confetti when the card is opened."
                                    on={formData.isConfetti}
                                    control={<Switch name="isConfetti" label="Confetti" checked={formData.isConfetti} onChange={handleInputChange} />}
                                >
                                    {formData.isConfetti && (
                                        <div className="flex flex-wrap items-center justify-between gap-3">
                                            <span className="text-sm text-gray-700">Style: <span className="font-semibold capitalize text-ink">{currentSettings?.style || 'snow'}</span></span>
                                            <Button type="button" variant="soft" size="sm" shape="pill" onClick={() => setIsConfettiSheetOpen(true)}>
                                                <Wand2 aria-hidden="true" /> Customise
                                            </Button>
                                        </div>
                                    )}
                                </ExtraCard>

                                <ExtraCard
                                    id="music"
                                    icon={MusicIcon}
                                    tone="heart"
                                    title="Background music"
                                    body="A song plays while the card is open."
                                    on={formData.isMusic}
                                    control={<Switch name="isMusic" label="Background music" checked={formData.isMusic} onChange={handleInputChange} />}
                                >
                                    {formData.isMusic && (
                                        <>
                                            <div className="flex flex-wrap items-center justify-between gap-3">
                                                <span className="min-w-0 truncate text-sm text-gray-700">Song: <span className="font-semibold text-ink">{musicSettings?.name || 'None chosen'}</span></span>
                                                <Button type="button" variant="soft" size="sm" shape="pill" onClick={() => setIsBottomSheetOpen(true)}>
                                                    <Volume2 aria-hidden="true" /> {musicSettings ? 'Change song' : 'Choose a song'}
                                                </Button>
                                            </div>
                                            {errors.music && <p className="mt-2 text-sm text-heart-700">{errors.music}</p>}
                                        </>
                                    )}
                                </ExtraCard>

                                <ExtraCard
                                    id="custom-logo"
                                    icon={Stamp}
                                    tone="sage"
                                    title="Your logo"
                                    body="Shown on every card and envelope. JPG, PNG or WebP up to 2 MB."
                                    on={!!formData.logoFile}
                                    control={
                                        <Button type="button" variant="soft" size="sm" shape="pill" onClick={() => logoFileInputRef.current?.click()}>
                                            {formData.logoFile ? 'Change' : 'Upload'}
                                        </Button>
                                    }
                                >
                                    {formData.logoFile && (
                                        <div className="flex items-center justify-between gap-3">
                                            <div className="flex min-w-0 items-center gap-3">
                                                <span className="flex h-12 w-16 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-paper-rule bg-white p-1">
                                                    {/* eslint-disable-next-line @next/next/no-img-element */}
                                                    <img src={formData.logoFile} alt="Your logo" className="max-h-full max-w-full object-contain" />
                                                </span>
                                                <span className="text-sm text-gray-700">
                                                    {formData.logoFile.startsWith('blob:') ? 'Uploads when you create the order' : 'From your default settings'}
                                                </span>
                                            </div>
                                            <button type="button" onClick={handleRemoveLogo} aria-label="Remove logo"
                                                className="inline-flex h-8 w-8 items-center justify-center rounded-full text-gray-500 hover:bg-heart-50 hover:text-heart-700">
                                                <Trash2 className="h-4 w-4" />
                                            </button>
                                        </div>
                                    )}
                                </ExtraCard>
                            </div>
                        </Panel>
                    )}

                    {/* STEP 4: envelope */}
                    {selectedTab === 'envelope' && (
                        <Panel
                            title="Envelope"
                            description="Every card arrives in this envelope. Click it to watch it open."
                            aside={
                                <Button type="button" variant="ghost" size="sm" shape="pill" onClick={handleResetAppearance} className="text-gray-700">
                                    <RotateCcw aria-hidden="true" /> Reset
                                </Button>
                            }
                        >
                            <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_340px] xl:grid-cols-[minmax(0,1fr)_380px]">
                                {/* Preview stays in view while the editor options scroll */}
                                <div className="flex flex-col items-center gap-5 rounded-2xl bg-paper px-4 py-8 sm:px-8 lg:sticky lg:top-[calc(var(--nav-height)+1.5rem)]">
                                    <div className="w-full">
                                        <EnvelopeStage zoom={zoom}>
                                            <Envelope
                                                key={animationKey}
                                                appearance={appearance}
                                                stampUrl={currentStamp}
                                                coverImage={coverImage}
                                                title={envelopeTitle}
                                                isRotator={false}
                                                onHide={() => { }}
                                                customLogo={formData.logoFile}
                                            />
                                        </EnvelopeStage>
                                    </div>
                                    <div className="flex items-center gap-3 rounded-full border border-paper-rule bg-white px-3 py-1.5 shadow-card">
                                        <button type="button" onClick={handleZoomOut} aria-label="Zoom out" className="rounded-full p-1.5 text-gray-600 hover:bg-paper hover:text-ink">
                                            <ZoomOut className="h-4 w-4" />
                                        </button>
                                        <span className="min-w-[3rem] text-center text-sm font-semibold tabular-nums text-ink">{zoom}%</span>
                                        <button type="button" onClick={handleZoomIn} disabled={zoom >= 100} aria-label="Zoom in" className="rounded-full p-1.5 text-gray-600 hover:bg-paper hover:text-ink disabled:opacity-30 disabled:hover:bg-transparent">
                                            <ZoomIn className="h-4 w-4" />
                                        </button>
                                        <span className="h-5 w-px bg-paper-rule" aria-hidden="true" />
                                        <Button type="button" size="sm" shape="pill" onClick={handlePlayAnimation}>
                                            <Play className="fill-current" aria-hidden="true" /> Replay
                                        </Button>
                                    </div>
                                    <p className="text-xs text-gray-600">Click the envelope to open it.</p>
                                </div>
                                <div className="min-w-0">
                                    <NewEnvelopeEditor
                                        appearance={appearance}
                                        onAppearanceChange={handleAppearanceChange}
                                        stampUrl={currentStamp}
                                        onStampChange={handleStampUpdate}
                                        customLogo={formData.logoFile}
                                        onLogoChange={handleLogoChange}
                                    />
                                </div>
                            </div>
                        </Panel>
                    )}

                    {/* Final step: the order in one line, next to Create */}
                    {selectedTab === 'envelope' && (
                        <div className="flex flex-wrap items-center gap-x-5 gap-y-2 rounded-2xl border border-paper-rule bg-white px-5 py-4 text-sm text-gray-700">
                            <span className="font-display text-base text-ink">{formData.collectionTitle?.trim() || 'Untitled order'}</span>
                            <span><span className="font-semibold text-ink">{recipientCount}</span> {recipientCount === 1 ? 'card' : 'cards'}</span>
                            <span><span className="font-semibold text-ink">{mySelectedCards.length}</span> {mySelectedCards.length === 1 ? 'design' : 'designs'}{mySelectedCards.length > 1 ? (isShuffle ? ', shuffled' : ', in order') : ''}</span>
                            {dateRange && <span>Sending {dateRange}</span>}
                            {extras.length > 0 && <span>{extras.join(' · ')}</span>}
                            {rowIssueCount > 0 && <span className="font-medium text-heart-700">{rowIssueCount} {rowIssueCount === 1 ? 'row' : 'rows'} to fix</span>}
                        </div>
                    )}

                    {/* Step navigation (desktop) */}
                    <div className="hidden border-t border-paper-rule pt-6 lg:block">
                        <NavButtons />
                    </div>
                </main>

                {/* Order summary (hidden on the envelope step so the preview gets the full width) */}
                <aside className={selectedTab === 'envelope' ? 'hidden' : 'hidden lg:block'}>
                    <div className="sticky top-[calc(var(--nav-height)+1.5rem)] rounded-2xl border border-paper-rule bg-white p-6 shadow-card">
                        <p className="text-xs font-semibold uppercase tracking-[0.14em] text-heart-600">Your order</p>
                        <h2 className="mt-2 truncate font-display text-xl text-ink">{formData.collectionTitle?.trim() || 'Untitled order'}</h2>
                        {formData.senderName.trim() && <p className="mt-0.5 truncate text-sm text-gray-600">From {formData.senderName.trim()}</p>}

                        <dl className="mt-5 space-y-4 border-t border-paper-rule pt-5 text-sm">
                            <div>
                                <dt className="text-gray-600">Designs</dt>
                                <dd className="mt-1.5">
                                    {mySelectedCards.length === 0 ? (
                                        <span className="text-gray-500">None chosen</span>
                                    ) : (
                                        <div className="flex items-center gap-2">
                                            <div className="flex -space-x-3">
                                                {mySelectedCards.slice(0, 4).map((c) => (
                                                    // eslint-disable-next-line @next/next/no-img-element
                                                    <img key={c.id} src={c.card_image} alt="" className="h-12 w-[2.15rem] rounded-md border-2 border-white object-cover shadow-sm" />
                                                ))}
                                            </div>
                                            <span className="font-semibold text-ink">
                                                {mySelectedCards.length}{mySelectedCards.length > 1 ? (isShuffle ? ', shuffled' : ', in order') : ''}
                                            </span>
                                        </div>
                                    )}
                                </dd>
                            </div>
                            <div className="flex justify-between gap-4">
                                <dt className="text-gray-600">People</dt>
                                <dd className="text-right font-semibold text-ink">
                                    {recipientCount || <span className="font-normal text-gray-500">None yet</span>}
                                    {rowIssueCount > 0 && <span className="block text-xs font-medium text-heart-700">{rowIssueCount} to fix</span>}
                                </dd>
                            </div>
                            <div className="flex justify-between gap-4">
                                <dt className="text-gray-600">Sending</dt>
                                <dd className="text-right font-semibold text-ink">
                                    {dateRange || <span className="font-normal text-gray-500">No dates yet</span>}
                                    {undatedCount > 0 && recipientCount > 0 && <span className="block text-xs font-medium text-gray-600">{undatedCount} without a date</span>}
                                </dd>
                            </div>
                            <div className="flex justify-between gap-4">
                                <dt className="text-gray-600">Time zone</dt>
                                <dd className="truncate text-right font-semibold text-ink">{formData.timeZone.replace(/_/g, ' ')}</dd>
                            </div>
                            <div className="flex justify-between gap-4">
                                <dt className="text-gray-600">Extras</dt>
                                <dd className="text-right font-semibold text-ink">{extras.length ? extras.join(', ') : <span className="font-normal text-gray-500">None</span>}</dd>
                            </div>
                        </dl>

                        <div className="mt-6 rounded-xl bg-paper px-4 py-3 text-sm text-gray-700">
                            <CalendarClock className="mb-1 h-4 w-4 text-heart-600" aria-hidden="true" />
                            Each card gets its own signing link, so your team can add messages before it sends. Free during launch.
                        </div>
                    </div>
                </aside>
            </div>

            {/* Mobile action bar */}
            <div className="fixed inset-x-0 bottom-0 z-30 border-t border-paper-rule bg-white/95 px-4 py-3 backdrop-blur lg:hidden">
                <div className="mx-auto flex max-w-3xl items-center justify-between gap-3">
                    <p className="min-w-0 truncate text-sm text-gray-700">
                        <span className="font-semibold text-ink">{recipientCount}</span> {recipientCount === 1 ? 'person' : 'people'}
                        <span className="mx-1.5 text-gray-400" aria-hidden="true">·</span>
                        <span className="font-semibold text-ink">{mySelectedCards.length}</span> {mySelectedCards.length === 1 ? 'design' : 'designs'}
                    </p>
                    <NavButtons compact />
                </div>
            </div>

            {/* --- MODALS / SHEETS --- */}
            <BottomSheet
                isOpen={isBottomSheetOpen}
                onClose={() => setIsBottomSheetOpen(false)}
                title="Choose a song"
                categories={categories}
                musicList={musicList}
                onSelect={handleSelectMusic}
            />

            <ConfettiSheet isOpen={isConfettiSheetOpen}
                onClose={() => setIsConfettiSheetOpen(false)}
                initialSettings={currentSettings}
                onSave={handleConfettiSettingsChange}
                onPreview={() => { }}
            />

            <BatchCardsSheet
                isOpen={isLibraryOpen}
                onClose={() => setIsLibraryOpen(false)}
                title="Choose designs"
                categorySlug="birthday-cards"
                initialSelectedCards={mySelectedCards}
                onSelect={handleSelectCards}
            />

            {/* Hidden file input for logo */}
            <input
                ref={logoFileInputRef}
                type="file"
                hidden
                accept="image/jpeg,image/png,image/webp"
                onChange={handleLogoFileSelected}
            />

            {/* Logo Cropper Modal */}
            {logoCropperSrc && (
                <ImageCropperModal
                    isOpen={isLogoCropperOpen}
                    imageSrc={logoCropperSrc}
                    aspectRatio={16 / 9}
                    cropType="logo"
                    title="Crop your logo"
                    onCropComplete={handleLogoCropComplete}
                    onCancel={() => setIsLogoCropperOpen(false)}
                />
            )}
        </div>
    );
}
